import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const NONE = "__none";

/** Generic remote select. items: [{id, label}] */
const RemoteSelect = ({ value, onChange, items, placeholder, noneLabel, testId, className, disabled }) => (
  <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? null : v)} disabled={disabled}>
    <SelectTrigger data-testid={testId} className={className}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="max-h-72">
      {noneLabel !== false && <SelectItem value={NONE}>{noneLabel || "Ikke valgt"}</SelectItem>}
      {(items || []).map((i) => <SelectItem key={i.id} value={i.id}>{i.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const UserSelect = ({ value, onChange, roles, noneLabel = "Ikke tildelt", testId = "user-select", className }) => {
  const { can } = useAuth();
  const { data } = useQuery({ queryKey: ["users"], queryFn: () => get("/users"), enabled: can("users:read") });
  const items = (data || []).filter((u) => u.active !== false && (!roles || roles.includes(u.role))).map((u) => ({ id: u.id, label: `${u.name} (${u.role})` }));
  return <RemoteSelect value={value} onChange={onChange} items={items} noneLabel={noneLabel} testId={testId} className={className} placeholder="Velg bruker" />;
};

export const CustomerSelect = ({ value, onChange, testId = "customer-select", className, noneLabel = "Velg kunde" }) => {
  const { data } = useQuery({ queryKey: ["customers"], queryFn: () => get("/customers") });
  return <RemoteSelect value={value} onChange={onChange} items={(data || []).map((c) => ({ id: c.id, label: c.name }))} noneLabel={noneLabel} testId={testId} className={className} placeholder="Velg kunde" />;
};

export const ProjectSelect = ({ value, onChange, testId = "project-select", className, noneLabel = "Ingen prosjekt" }) => {
  const { data } = useQuery({ queryKey: ["projects"], queryFn: () => get("/projects") });
  return <RemoteSelect value={value} onChange={onChange} items={(data || []).map((p) => ({ id: p.id, label: `${p.project_number} – ${p.name}` }))} noneLabel={noneLabel} testId={testId} className={className} placeholder="Velg prosjekt" />;
};

export const ModuleTypeSelect = ({ value, onChange, testId = "module-type-select", className, noneLabel = "Alle typer" }) => {
  const { data } = useQuery({ queryKey: ["module-types"], queryFn: () => get("/module-types") });
  return <RemoteSelect value={value} onChange={onChange} items={(data || []).map((t) => ({ id: t.id, label: `${t.code} – ${t.name}` }))} noneLabel={noneLabel} testId={testId} className={className} placeholder="Modultype" />;
};

export const SupplierSelect = ({ value, onChange, category, testId = "supplier-select", className, noneLabel = "Ingen leverandør" }) => {
  const { data } = useQuery({ queryKey: ["suppliers"], queryFn: () => get("/suppliers") });
  const items = (data || []).filter((s) => s.active !== false && (!category || s.category === category || (s.categories || []).includes(category))).map((s) => ({ id: s.id, label: `${s.name} (${s.category})` }));
  return <RemoteSelect value={value} onChange={onChange} items={items} noneLabel={noneLabel} testId={testId} className={className} placeholder="Leverandør" />;
};

export const ModuleSelect = ({ value, onChange, testId = "module-select", className, noneLabel = "Velg modul", filter }) => {
  const { data } = useQuery({ queryKey: ["modules"], queryFn: () => get("/modules") });
  const items = (data || []).filter((m) => !filter || filter(m)).map((m) => ({ id: m.id, label: `${m.module_id} – ${m.type_name || ""} (${m.status})` }));
  return <RemoteSelect value={value} onChange={onChange} items={items} noneLabel={noneLabel} testId={testId} className={className} placeholder="Modul" />;
};

/** Static enum select with Norwegian labels */
export const EnumSelect = ({ value, onChange, options, labelFn, allLabel, testId, className, placeholder }) => (
  <Select value={value || (allLabel ? "all" : undefined)} onValueChange={(v) => onChange(v)}>
    <SelectTrigger data-testid={testId} className={className}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent className="max-h-72">
      {allLabel && <SelectItem value="all">{allLabel}</SelectItem>}
      {options.map((o) => <SelectItem key={o} value={o}>{labelFn ? labelFn(o) : o}</SelectItem>)}
    </SelectContent>
  </Select>
);
